import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { type RouterOutputs, trpc } from '../lib/trpc';
import {
  buildContactsSearchInput,
  CONTACTS_PAGE_SIZE,
  type ContactSortBy,
  type ContactSortDir,
  type ContactsFilters,
  DEFAULT_FILTERS,
  deriveContactsPage,
  hasActiveContactsFilters,
  useContactsSearchState,
} from './contacts-search-core';

export type { ContactSortBy, ContactSortDir, ContactsFilters } from './contacts-search-core';
export { hasActiveContactsFilters };

export type ContactListItem = RouterOutputs['contacts']['search']['items'][number];

const VALID_SORT_BY: ContactSortBy[] = [
  'label',
  'displayName',
  'companyName',
  'createdAt',
  'updatedAt',
];
const VALID_SORT_DIR: ContactSortDir[] = ['asc', 'desc'];

function filtersFromParams(params: URLSearchParams): ContactsFilters {
  const typeId = params.get('type');
  const ownerId = params.get('owner');
  const sortBy = params.get('sortBy');
  const sortDir = params.get('sortDir');
  return {
    typeId: typeId ? Number(typeId) : undefined,
    ownerId: ownerId ? Number(ownerId) : undefined,
    companyName: params.get('company') || undefined,
    hasEmail: params.get('hasEmail') === 'true' ? true : undefined,
    hasPhone: params.get('hasPhone') === 'true' ? true : undefined,
    sortBy: VALID_SORT_BY.includes(sortBy as ContactSortBy)
      ? (sortBy as ContactSortBy)
      : DEFAULT_FILTERS.sortBy,
    sortDir: VALID_SORT_DIR.includes(sortDir as ContactSortDir)
      ? (sortDir as ContactSortDir)
      : DEFAULT_FILTERS.sortDir,
  };
}

function paramsFromState(
  debouncedQuery: string,
  filters: ContactsFilters,
  cursor: number
): Record<string, string | null> {
  return {
    q: debouncedQuery || null,
    type: filters.typeId ? String(filters.typeId) : null,
    owner: filters.ownerId ? String(filters.ownerId) : null,
    company: filters.companyName || null,
    hasEmail: filters.hasEmail ? 'true' : null,
    hasPhone: filters.hasPhone ? 'true' : null,
    sortBy: filters.sortBy !== DEFAULT_FILTERS.sortBy ? filters.sortBy : null,
    sortDir: filters.sortDir !== DEFAULT_FILTERS.sortDir ? filters.sortDir : null,
    cursor: cursor > 0 ? String(cursor) : null,
  };
}

/**
 * Drives the Contacts list: debounced free-text search (server-side FTS via
 * `contacts.search`), filter state, and offset pagination. Shared by both portals
 * so the search/filter/pagination behavior — and its query-key shape — stays
 * identical rather than drifting per app. State/derivation logic lives in
 * `contacts-search-core.ts` so the extension can wrap the same behavior against
 * its own tRPC client.
 *
 * URL-backed (`?q=`/`?type=`/`?owner=`/`?company=`/`?hasEmail=`/`?hasPhone=`/
 * `?sortBy=`/`?sortDir=`/`?cursor=`) so a filtered/sorted/paged view is shareable
 * via link — portal-only; the extension keeps `useContactsSearchState`'s plain
 * local state, since a browser-extension popup has no meaningful "shareable URL".
 */
export function useContactsSearch() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Lazy useState initializer — guaranteed by React to run exactly once, at
  // mount — seeds first-mount state from the URL. `state` below is the source
  // of truth afterward and mirrors out to the URL, matching
  // useContactsSearchState's existing debounce-then-commit architecture
  // rather than fighting it.
  const [initial] = React.useState(() => ({
    searchInput: searchParams.get('q') ?? '',
    filters: filtersFromParams(searchParams),
    cursor: Number(searchParams.get('cursor')) || 0,
  }));

  const state = useContactsSearchState(initial);

  // Read via ref rather than depending on `searchParams` directly: it should
  // only mirror when the *committed* search state changes, not fire again
  // (redundantly, or worse in a loop if the router hands back a new
  // URLSearchParams identity per render) whenever the URL updates for any
  // reason, including from this same effect's own writes.
  const searchParamsRef = React.useRef(searchParams);
  searchParamsRef.current = searchParams;

  React.useEffect(() => {
    const nextParams = new URLSearchParams(searchParamsRef.current);
    const updates = paramsFromState(state.debouncedQuery, state.filters, state.cursor);
    for (const [key, value] of Object.entries(updates)) {
      if (value === null) nextParams.delete(key);
      else nextParams.set(key, value);
    }
    setSearchParams(nextParams, { replace: true });
  }, [state.debouncedQuery, state.filters, state.cursor, setSearchParams]);

  const query = trpc.contacts.search.useQuery(buildContactsSearchInput(state));

  const totalCount = query.data?.totalCount ?? 0;
  const items = query.data?.items ?? [];

  return {
    searchInput: state.searchInput,
    setSearchInput: state.setSearchInput,
    filters: state.filters,
    setFilter: state.setFilter,
    resetFilters: state.resetFilters,
    hasActiveFilters: hasActiveContactsFilters(state.searchInput, state.filters),
    items,
    totalCount,
    isLoading: query.isLoading,
    isError: query.isError,
    ...deriveContactsPage(state.cursor, items, totalCount, query.data?.nextCursor),
    goToNextPage: () => {
      if (query.data?.nextCursor != null) state.setCursor(query.data.nextCursor);
    },
    goToPrevPage: () => state.setCursor((c) => Math.max(0, c - CONTACTS_PAGE_SIZE)),
    pageSize: CONTACTS_PAGE_SIZE,
  };
}
